import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Send, ExternalLink, SendHorizontal, MessageCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { t } from "../lib/i18n";
import { getToken } from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const mins = d.getMinutes().toString().padStart(2, "0");
  const hours = d.getHours().toString().padStart(2, "0");
  // Today → show time only
  if (d.toDateString() === now.toDateString()) {
    return `${hours}:${mins}`;
  }
  // Yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) {
    return `вчера ${hours}:${mins}`;
  }
  // Older
  return `${d.getDate().toString().padStart(2, "0")}.${(d.getMonth() + 1).toString().padStart(2, "0")} ${hours}:${mins}`;
}

function groupMessagesByDate(msgs) {
  if (!msgs.length) return [];
  const groups = [];
  let currentDate = null;
  let currentGroup = [];
  for (const m of msgs) {
    const d = new Date(m.created_at).toDateString();
    if (d !== currentDate) {
      if (currentGroup.length) groups.push({ date: currentDate, msgs: currentGroup });
      currentDate = d;
      currentGroup = [m];
    } else {
      currentGroup.push(m);
    }
  }
  if (currentGroup.length) groups.push({ date: currentDate, msgs: currentGroup });
  return groups;
}

function formatDateLabel(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return "Сегодня";
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "Вчера";
  return d.toLocaleDateString("ru-RU", { day: "numeric", month: "long" });
}

export default function Chat() {
  const { matchId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const lang = user?.language || "ru";
  const myId = user?.id;
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [otherUser, setOtherUser] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [otherTyping, setOtherTyping] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const endRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimerRef = useRef(null);
  const lastTypingSentRef = useRef(0);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/chat/${matchId}`);
      setMsgs(data.messages || []);
      const { data: matches } = await api.get(`/matches`);
      const m = matches.find((x) => x.match_id === matchId);
      if (m) setOtherUser(m.user);
    } catch {
      toast.error(t(lang, "err_open_chat"));
      nav("/matches");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [matchId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs.length, otherTyping]);

  // WebSocket connection with reconnect
  useEffect(() => {
    let cancelled = false;
    let reconnectTimer = null;

    const connect = () => {
      const token = getToken();
      if (!token) return;
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const wsUrl =
        backend.replace(/^http/, "ws") +
        `/api/ws/chat/${matchId}?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelled) return;
        setWsConnected(true);
      };
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data.type === "message" && data.message) {
            setMsgs((prev) => {
              if (prev.some((m) => m.id === data.message.id)) return prev;
              return [...prev, data.message];
            });
          } else if (data.type === "typing") {
            if (data.user_id !== user?.id) {
              setOtherTyping(!!data.typing);
              if (data.typing) {
                clearTimeout(typingTimerRef.current);
                typingTimerRef.current = setTimeout(() => setOtherTyping(false), 4000);
              }
            }
          }
        } catch {}
      };
      ws.onclose = () => {
        setWsConnected(false);
        if (!cancelled) {
          reconnectTimer = setTimeout(connect, 2000);
        }
      };
      ws.onerror = () => { /* onclose will handle reconnect */ };
    };

    connect();
    return () => {
      cancelled = true;
      clearTimeout(reconnectTimer);
      clearTimeout(typingTimerRef.current);
      try { wsRef.current?.close(); } catch {}
    };
    // eslint-disable-next-line
  }, [matchId]);

  const sendTypingSignal = (isTyping) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) return;
    const now = Date.now();
    if (isTyping && now - lastTypingSentRef.current < 1500) return;
    lastTypingSentRef.current = now;
    try {
      ws.send(JSON.stringify({ type: "typing", typing: isTyping }));
    } catch {}
  };

  const onInputChange = (e) => {
    setText(e.target.value);
    sendTypingSignal(e.target.value.length > 0);
  };

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    sendTypingSignal(false);
    try {
      const { data } = await api.post(`/chat/${matchId}`, { text: text.trim(), kind: "text" });
      setMsgs((m) => (m.some((x) => x.id === data.id) ? m : [...m, data]));
      setText("");
      // Focus input after send
      inputRef.current?.focus();
    } catch (e) {
      toast.error(e.response?.data?.detail || t(lang, "err_send"));
    } finally {
      setSending(false);
    }
  };

  const shareTelegram = async () => {
    if (!user?.username) {
      toast.error(t(lang, "no_tg_username"));
      setShareOpen(false);
      return;
    }
    try {
      const { data } = await api.post(`/chat/${matchId}`, { kind: "telegram_share" });
      setMsgs((m) => (m.some((x) => x.id === data.id) ? m : [...m, data]));
      setShareOpen(false);
      toast.success(t(lang, "tg_sent_toast"));
    } catch (e) {
      toast.error(t(lang, "tg_send_error"));
    }
  };

  const groupedMessages = useMemo(() => groupMessagesByDate(msgs), [msgs]);

  return (
    <div className="kupidon-shell safe-top safe-bottom flex flex-col relative" data-testid="chat-page">
      {/* Decorative gradient */}
      <div className="absolute top-0 left-0 right-0 h-40 bg-gradient-to-b from-[color:var(--peach)]/8 to-transparent pointer-events-none" />

      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[color:var(--border)] bg-white/60 backdrop-blur-xl relative z-10">
        <button
          data-testid="chat-back"
          onClick={() => nav("/matches")}
          className="w-9 h-9 rounded-full bg-white/70 backdrop-blur flex items-center justify-center hover:bg-white active:scale-95 transition-all shadow-sm"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          type="button"
          className="flex items-center gap-3 flex-1 min-w-0"
          onClick={() => {
            if (otherUser?.id) {
              nav(`/person/${otherUser.id}`, {
                state: { profile: otherUser, backTo: `/chat/${matchId}`, matched: true },
              });
            }
          }}
        >
          <div className="relative flex-shrink-0">
            <div className="w-10 h-10 rounded-full overflow-hidden ring-2 ring-white/60 shadow-sm">
              {otherUser?.photos?.[0]?.data_url ? (
                <img
                  src={otherUser.photos[0].data_url}
                  alt=""
                  className={`w-full h-full object-cover ${otherUser.photos[0].blurred ? "photo-blur" : ""}`}
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-[color:var(--peach)]/30 to-[color:var(--peach-deep)]/20 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-[color:var(--peach-deep)]" />
                </div>
              )}
            </div>
            <div
              className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${
                wsConnected ? "bg-[color:var(--teal-superlike)]" : "bg-[color:var(--border)]"
              }`}
            />
          </div>
          <div className="flex-1 min-w-0 text-left">
            <div className="font-[Onest] font-bold text-base truncate">{otherUser?.first_name || "…"}</div>
            <div className="text-[11px] text-[color:var(--ink-soft)] h-3.5">
              {otherTyping ? (
                <span className="text-[color:var(--teal-superlike)] font-medium" data-testid="chat-typing-indicator">
                  {t(lang, "typing")}
                </span>
              ) : (
                wsConnected ? otherUser?.city || "online" : "connecting…"
              )}
            </div>
          </div>
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto no-scrollbar px-4 py-4" data-testid="chat-messages">
        {msgs.length === 0 && (
          <div className="flex flex-col items-center justify-center text-center py-20 px-8">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[color:var(--peach)]/20 to-[color:var(--peach-deep)]/10 flex items-center justify-center mb-4 shadow-inner">
              <MessageCircle className="w-9 h-9 text-[color:var(--peach-deep)]" />
            </div>
            <div className="font-[Onest] font-bold text-lg text-[color:var(--ink)]">{t(lang, "chat_start")}</div>
            <div className="text-sm text-[color:var(--ink-soft)] mt-2 max-w-[24ch] leading-relaxed">
              {t(lang, "say_hi")}
            </div>
          </div>
        )}

        <AnimatePresence initial={false}>
          {groupedMessages.map((group) => (
            <div key={group.date}>
              {/* Date separator */}
              <div className="flex justify-center my-4">
                <div className="text-[10px] uppercase tracking-[0.15em] text-[color:var(--ink-soft)] bg-white/60 backdrop-blur px-3 py-1 rounded-full border border-[color:var(--border)] font-medium">
                  {formatDateLabel(group.date)}
                </div>
              </div>

              {group.msgs.map((m) => {
                const mine = m.from_user_id === myId;
                const time = formatTime(m.created_at);
                return (
                  <motion.div
                    key={m.id}
                    initial={{ opacity: 0, y: 8, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ ease: [0.16, 1, 0.3, 1], duration: 0.25 }}
                    className={`flex mb-2 ${mine ? "justify-end" : "justify-start"}`}
                  >
                    {m.kind === "telegram_share" ? (
                      <div
                        data-testid={`msg-telegram-${m.id}`}
                        className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                          mine
                            ? "bg-gradient-to-r from-[color:var(--peach)] to-[color:var(--peach-deep)] text-white rounded-br-sm"
                            : "bg-white text-[color:var(--ink)] rounded-bl-sm border border-[color:var(--border)]"
                        }`}
                      >
                        <div className="flex items-center gap-2.5">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${mine ? "bg-white/20" : "bg-[color:var(--peach)]/10"}`}>
                            <SendHorizontal className="w-4 h-4" />
                          </div>
                          <div>
                            <div className="text-[11px] opacity-70 font-medium">{t(lang, "telegram_sent_label")}</div>
                            {m.telegram_username ? (
                              <a
                                href={`https://t.me/${m.telegram_username}`}
                                target="_blank"
                                rel="noreferrer"
                                className="text-sm font-bold flex items-center gap-1 hover:underline"
                              >
                                @{m.telegram_username}
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            ) : (
                              <div className="text-sm font-bold">{t(lang, "telegram_hidden")}</div>
                            )}
                          </div>
                        </div>
                        <div className={`text-[10px] mt-1.5 text-right ${mine ? "opacity-60" : "text-[color:var(--ink-soft)]"}`}>
                          {time}
                        </div>
                      </div>
                    ) : (
                      <div
                        className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-[15px] leading-snug relative ${
                          mine
                            ? "bg-gradient-to-br from-[color:var(--peach)] to-[color:var(--peach-deep)] text-white rounded-br-sm"
                            : "bg-white text-[color:var(--ink)] rounded-bl-sm border border-[color:var(--border)] shadow-sm"
                        }`}
                      >
                        {m.text}
                        <div className={`text-[10px] mt-1 text-right ${mine ? "opacity-60" : "text-[color:var(--ink-soft)]"}`}>
                          {time}
                        </div>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          ))}
        </AnimatePresence>

        {otherTyping && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-start mb-2"
            data-testid="typing-bubble"
          >
            <div className="rounded-2xl px-4 py-3 bg-white border border-[color:var(--border)] flex gap-1.5 items-center rounded-bl-sm shadow-sm">
              {[0, 1, 2].map((i) => (
                <motion.span
                  key={i}
                  className="w-2 h-2 rounded-full bg-[color:var(--peach)]"
                  animate={{ y: [0, -5, 0], opacity: [0.4, 1, 0.4] }}
                  transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.18 }}
                />
              ))}
            </div>
          </motion.div>
        )}
        <div ref={endRef} />
      </div>

      {/* Composer */}
      <div className="px-3 pb-3 pt-2 border-t border-[color:var(--border)] bg-white/60 backdrop-blur-xl relative z-10">
        <div className="flex items-center gap-2">
          <button
            data-testid="chat-send-telegram"
            onClick={() => setShareOpen(true)}
            className="flex items-center gap-1.5 px-3 h-11 rounded-full bg-gradient-to-r from-[#2AABEE]/10 to-[#2AABEE]/5 border border-[#2AABEE]/20 text-[#2AABEE] hover:bg-[#2AABEE]/15 active:scale-95 transition-all flex-shrink-0"
            aria-label="Отправить Telegram"
          >
            <SendHorizontal className="w-4 h-4" />
            <span className="text-[11px] font-semibold hidden sm:inline">TG</span>
          </button>
          <input
            ref={inputRef}
            data-testid="chat-input"
            value={text}
            onChange={onInputChange}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            onBlur={() => sendTypingSignal(false)}
            placeholder={t(lang, "chat_input_ph")}
            className="flex-1 px-4 py-3 rounded-full bg-white/90 border border-[color:var(--border)] focus:outline-none focus:ring-2 focus:ring-[color:var(--peach)]/40 text-[15px] placeholder:text-[color:var(--ink-soft)]/50"
          />
          <button
            data-testid="chat-send"
            onClick={send}
            disabled={!text.trim() || sending}
            className="w-11 h-11 rounded-full bg-gradient-to-br from-[color:var(--peach)] to-[color:var(--peach-deep)] text-white flex items-center justify-center disabled:opacity-30 disabled:scale-100 hover:scale-105 active:scale-95 transition-all flex-shrink-0 shadow-md"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="rounded-3xl max-w-sm">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-12 h-12 rounded-full bg-[#2AABEE]/10 flex items-center justify-center">
                <SendHorizontal className="w-6 h-6 text-[#2AABEE]" />
              </div>
              <div>
                <DialogTitle className="text-lg font-[Onest]">{t(lang, "share_tg_title")}</DialogTitle>
                <DialogDescription className="text-sm mt-1">
                  {t(lang, "share_tg_desc", { u: user?.username || "profile" })}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <DialogFooter className="gap-2 mt-2">
            <button
              data-testid="chat-share-cancel"
              onClick={() => setShareOpen(false)}
              className="flex-1 py-2.5 rounded-full bg-white text-[color:var(--ink)] border border-[color:var(--border)] font-medium text-sm hover:bg-[color:var(--peach-bg)] active:scale-[0.98] transition-all"
            >
              {t(lang, "cancel")}
            </button>
            <button
              data-testid="chat-share-confirm"
              onClick={shareTelegram}
              className="flex-1 py-2.5 rounded-full bg-gradient-to-r from-[#2AABEE] to-[#229ED9] text-white font-medium text-sm hover:brightness-105 active:scale-[0.98] transition-all shadow-md"
            >
              {t(lang, "send")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
